import React from 'react'
import Tooltip from '@mui/material/Tooltip'
import GreenIcon from '../../../images/Green Icon.png'

/**
 * Step component represents a single step in the progress bar with styling and an interactive click event.
 *
 * @component
 * @memberof Components
 * @param {Object} props - The component props.
 * @param {number} props.number - The step number.
 * @param {string} props.title1 - The main title of the step.
 * @param {string} props.title2 - The subtitle or additional description of the step.
 * @param {string} props.status - The status of the step. Possible values:
 * - `"done"`: The step is completed.
 * - `"current"`: The step is currently active.
 * - `"current objection"`: The step is active but has an objection.
 * - `""` (empty string): The step is pending.
 * @param {Function} props.openSubPopUp - Function to open a popup when a step is clicked.
 * @param {string} props.pageName - The name of the page, used to determine dynamic styling.
 *
 * @example
 * <Step 
 *   number={1} 
 *   title1="Step 1" 
 *   title2="Description" 
 *   status="done" 
 *   openSubPopUp={(title, status) => console.log(title, status)} 
 *   pageName="New Connection" 
 * />
 *
 * @returns {JSX.Element} The Step component representing a single step in the progress bar.
 */
const Step = (props) => {
    const disableStepClick = props.disableStepClick !== false;

    const newConnStyle = { minWidth: '9.7%', width: 'auto' }

    const mcrWithSiteMNTStyle = { minWidth: '7%', width: 'auto', maxWidth: '9.5%' }

    const mrcStyle = { minWidth: 0, width: 'auto', maxWidth: 'none' }

    const mcrWithLabMNTStyle = { minWidth: '5%', width: 'auto', maxWidth: '7.6%' }

    const rcStyle = {}

    const coaStyle = {}

    const mcrWithOutMNTStyle = { minWidth: '10.9%', width: 'auto' }

    const duedateExtensionStyle = { minWidth: '49%', width: 'auto' }

    const billCorrectionStyle = { minWidth: '49%', width: 'auto' }

    const installmentsStyle = { minWidth: '49%', width: 'auto' }

    const adjustLastBillStyle = { minWidth: '33%', width: 'auto' }

    const withdrawalOfInstallmentStyle = { minWidth: '33%', width: 'auto' }

    const setAsideStyle = { minWidth: '49.5%', width: 'auto' }

    const defaultStyle = { minWidth: '9.7%', width: 'auto' }

    const complaintsStyle = { minWidth: '19.75%', width: 'auto' }

    const isInstallmentsRejectedStep =
        props.pageName === 'Installments' && props.status === 'current objection';

    const isDdeRejectedStep =
        props.pageName === 'Due Date Extension' && props.status === 'current objection';

    const isAlbRejectedStep =
        props.pageName === 'Adjust Last Bill' && props.status === 'current objection';

    const isWoiRejectedStep =
        props.pageName === 'Withdrawal of Installment' && props.status === 'current objection';

    const stepClassName =
        props.status === "done"
            ? `step done${props.hideDoneCheckmark ? ' dde-step-numbered' : ''}${disableStepClick ? ' step--no-click' : ''}`
            : props.status === "current"
                ? `step current${disableStepClick ? ' step--no-click' : ''}`
                : props.status === "current objection"
                    ? `step currentObjection${isInstallmentsRejectedStep ? ' installments-rejected' : ''}${isDdeRejectedStep ? ' dde-rejected' : ''}${isAlbRejectedStep ? ' alb-rejected' : ''}${isWoiRejectedStep ? ' woi-rejected' : ''}${disableStepClick ? ' step--no-click' : ''}`
                    : `step${disableStepClick ? ' step--no-click' : ''}`;

    const classNameWithTooltip = `${stepClassName}${props.tooltip ? ' step--has-tooltip' : ''}`;

    const showStepNumber = props.status !== 'done' || props.hideDoneCheckmark;

    const stepInner = (
        <div className='span' style={props.tooltip ? { cursor: 'help', width: '100%' } : { width: '100%' }}>
            <div className='span-content'>
                <div className='icon-button'>
                    {showStepNumber ? props.number : <img src={GreenIcon} alt="Arear paid bill" width="20" />}
                </div>
            </div>
            <div className='step-title'>
                <span style={{ marginBottom: '-5px', display: 'block' }}>
                    {props.title1}
                </span>
                <span style={{ marginTop: '-5px', display: 'block' }}>
                    {props.title2}
                </span>
            </div>
        </div>
    );

    const stepBox = (
        <div
            className={classNameWithTooltip}
            onClick={() => {
                if (disableStepClick) return;
                props.openSubPopUp(props.title1 + ' ' + props.title2, props.status);
            }}
            style={
                props.pageName === 'New Connection' ? newConnStyle :
                    props.pageName === 'Due Date Extension' ? duedateExtensionStyle :
                        props.pageName === 'Adjust Last Bill' ? adjustLastBillStyle :
                        props.pageName === 'Withdrawal of Installment' ? withdrawalOfInstallmentStyle :
                        props.pageName === 'Installments' ? installmentsStyle :
                            props.pageName === 'Set Aside' ? setAsideStyle :
                                props.pageName === 'Meter Reading Correction' ? mrcStyle :
                                    props.pageName === 'Meter Change Request With Site MNT' ? mcrWithSiteMNTStyle :
                                        props.pageName === 'Meter Change Request With Lab MNT' ? mcrWithLabMNTStyle :
                                            props.pageName === 'Meter Change Request Without MNT' ? mcrWithOutMNTStyle :
                                                props.pageName === 'Power Complaints' ? complaintsStyle :
                                                    props.pageName === 'Reconnection' ? rcStyle :
                                                    props.pageName === 'Meter Change Order' ? rcStyle :
                                                    props.pageName === 'Change of Name' ? coaStyle :
                                                    props.pageName === 'Change of Attributes' ? coaStyle :
                                                        props.pageName === 'Bill Correction' ? billCorrectionStyle
                                                            : defaultStyle}
        >
            {stepInner}
        </div>
    );

    if (!props.tooltip) return stepBox;

    return (
        <Tooltip
            title={props.tooltip}
            arrow
            placement="bottom"
            enterDelay={150}
            enterTouchDelay={0}
            PopperProps={{ sx: { zIndex: 14000 } }}
        >
            {stepBox}
        </Tooltip>
    )
}

export default Step